/**
 * Atlas PM · 项目星图 · 节点语义图例（左下角，对齐原型 #starlg）
 *
 * 文案与颜色对齐原型 `index.html` 的 `#starlg`：
 *   已冻结（审批通过）/ 进行中 / 审批中 / 已驳回 / 逾期 / 草稿
 *   及三行说明。
 *
 * ⚠️ P6.46【用户授权的有意偏离原型】：原型 #starlg 原文为
 *   「已退回 / 逾期」「未开始」，本次统一状态中文名后改为
 *   「已驳回 / 逾期」「草稿」，与任务状态枚举（REJECTED / DRAFT）保持一致，
 *   避免图例与节点悬浮卡/详情页同名状态显示不同文案。
 *
 * ⭐ P8.19：图例可点击，筛选右侧任务列表（同一行再次点击取消筛选）。
 *   分桶规则（starLegendBucketOf）同时用于顶部状态计数条与列表筛选，
 *   保证「计数」与「筛选出的行数」永远一致。
 *   ⭐ P8.21：REJECTED 从「草稿」桶移入「已驳回 / 逾期」桶，
 *      与图例语义一致。逾期优先判断保持不变。
 */

export type StarLegendKey = 'frozen' | 'inProgress' | 'approving' | 'rejectedOverdue' | 'draft';

export interface StarLegendBucketInput {
  state: string;
  overdueDays?: number;
}

/** 任务 → 图例桶（null = 不参与筛选，如归档 / 取消 / 解锁编辑中） */
export function starLegendBucketOf(task: StarLegendBucketInput): StarLegendKey | null {
  const overdueDays = task.overdueDays ?? 0;
  const locked = task.state === 'FROZEN' || task.state === 'ARCHIVED' || task.state === 'CANCELLED';
  if (overdueDays > 0 && !locked) return 'rejectedOverdue';
  switch (task.state) {
    case 'IN_PROGRESS':
      return 'inProgress';
    case 'FROZEN':
      return 'frozen';
    case 'SUBMITTED':
    case 'APPROVING':
      return 'approving';
    case 'REJECTED':
      return 'rejectedOverdue';
    case 'DRAFT':
      return 'draft';
    default:
      return null;
  }
}

const ITEMS: ReadonlyArray<{ key: StarLegendKey; color: string; label: string }> = [
  { key: 'frozen', color: '#a77bff', label: '已冻结（审批通过）' },
  { key: 'inProgress', color: '#5ee7ff', label: '进行中' },
  { key: 'approving', color: '#f0c060', label: '审批中' },
  { key: 'rejectedOverdue', color: '#ff6b8a', label: '已驳回 / 逾期' },
  { key: 'draft', color: '#8fb2d0', label: '草稿' },
];

export interface StarLegendProps {
  active: StarLegendKey | null;
  onToggle: (key: StarLegendKey) => void;
}

export default function StarLegend({ active, onToggle }: StarLegendProps) {
  return (
    <div className="star-legend" aria-label="节点语义">
      <div className="star-legend__title">节点语义</div>
      {ITEMS.map((item) => (
        <button
          type="button"
          className={[
            'star-legend__item',
            active === item.key ? 'star-legend__item--active' : '',
          ].join(' ')}
          key={item.key}
          onClick={() => onToggle(item.key)}
          aria-pressed={active === item.key}
          aria-label={`筛选：仅显示${item.label}任务`}
          title={`筛选：仅显示${item.label}任务`}
        >
          <span className="star-legend__dot" style={{ background: item.color }} />
          {item.label}
        </button>
      ))}
      <div className="star-legend__note">
        节点大小 = 指标数量 + 逾期加权
        <br />
        绕节点的小环为动态星轨
        <br />
        同色聚集 = 同一任务形态（星座）
      </div>
    </div>
  );
}
